import { LlmVoiceAgent, McpToolClient, OfflineVoiceAgent, ProxyProvider } from '@rally/agent';
import type { AgentEvent, ElicitationAnswer, ElicitationRequest, Receipt, TurnResult } from '@rally/agent';
import { demoHeaders } from './api.ts';
import { srv } from './server.ts';

/**
 * The simulated Alexa+ host, running in the browser.
 *
 * It is the MCP client Alexa+ would be: it connects to Rally's /mcp endpoint
 * over Streamable HTTP (a real network hop, visible in the receipts panel),
 * plans each turn (offline rules, or an LLM through the server's proxy),
 * renders MCP Apps views, and answers elicitations with an on-screen sheet.
 * Only an explicit yes to what Rally just proposed ("Yes, send them") answers
 * a sheet by itself. A command ("book the first one", "move it to Sunday")
 * gets the read-back spoken first, and waits for a yes or a tap.
 */

export type HostEvent =
  | { type: 'user'; text: string }
  | { type: 'alexa'; text: string; planner: string; latencyMs: number }
  | { type: 'system'; text: string }
  | { type: 'receipt'; receipt: Receipt }
  | { type: 'agent'; event: AgentEvent }
  | { type: 'elicitation'; request: ElicitationRequest; auto: boolean; answer: (a: ElicitationAnswer) => void }
  | { type: 'elicitation_done' }
  | { type: 'busy'; on: boolean };

export type HostOptions = {
  household: string;
  /** Empty when Alexa can't tell who is speaking. */
  speaker: string;
  /** An organizer's private planning link, which acts as them instead of a voice. */
  link?: string;
  era: 'legacy' | 'auto';
  planner: 'offline' | 'llm';
  llmLabel?: string | null;
  skill?: string;
  onEvent: (e: HostEvent) => void;
};

// The whole utterance is a yes ("Yes", "Yeah, go ahead", "Sure, do it"). "Yeah, cancel it" is a new
// command, so its read-back is spoken and waits.
const CONSENT = /^(?:yes|yeah|yep|yup|sure|ok|okay|go ahead|do it|please do|confirm|absolutely)(?:[\s,]+(?:please|go ahead|do it|send them|send it|book it|that's right|sounds good))*[\s.!]*$/i;

export class RallyHost {
  readonly client: McpToolClient;
  readonly offline: OfflineVoiceAgent;
  readonly llm?: LlmVoiceAgent;
  #lastUtterance = '';
  #closed = false;
  #pendingAnswer?: (a: ElicitationAnswer) => void;

  constructor(readonly opts: HostOptions) {
    this.client = new McpToolClient({
      url: srv('/mcp'),
      era: opts.era,
      headers: opts.link ? { 'x-rally-link': opts.link } : { 'x-rally-household': opts.household, ...demoHeaders(), ...(opts.speaker ? { 'x-rally-speaker': opts.speaker } : {}) },
      clientName: 'Alexa+ Simulator (Rally)',
      onReceipt: (receipt) => opts.onEvent({ type: 'receipt', receipt }),
      onElicit: (request) =>
        new Promise<ElicitationAnswer>((resolve) => {
          const auto = CONSENT.test(this.#lastUtterance.trim());
          const answer = (a: ElicitationAnswer) => {
            if (this.#pendingAnswer !== answer) return;
            this.#pendingAnswer = undefined;
            opts.onEvent({ type: 'elicitation_done' });
            resolve(a);
          };
          this.#pendingAnswer = answer;
          opts.onEvent({ type: 'elicitation', request, auto, answer });
          if (auto) setTimeout(() => answer({ action: 'accept', content: { confirm: true } }), 1300);
        }),
    });
    this.offline = new OfflineVoiceAgent({ tools: this.client });
    if (opts.planner === 'llm' && opts.llmLabel) {
      this.llm = new LlmVoiceAgent({ llm: new ProxyProvider(srv('/api/llm'), opts.llmLabel), tools: this.client, skill: opts.skill });
    }
  }

  /** A spoken yes or no while a confirmation sheet is open answers the sheet. */
  answerOpenSheet(text: string): boolean {
    if (!this.#pendingAnswer) return false;
    if (/^(yes|yeah|yep|sure|ok|okay|confirm|do it|go ahead)\b/i.test(text)) {
      this.#pendingAnswer({ action: 'accept', content: { confirm: true } });
      return true;
    }
    if (/^(no|nope|cancel|stop|wait|don't)\b/i.test(text)) {
      this.#pendingAnswer({ action: 'decline' });
      return true;
    }
    return false;
  }

  async say(text: string): Promise<TurnResult | undefined> {
    const { onEvent } = this.opts;
    if (this.answerOpenSheet(text)) {
      onEvent({ type: 'user', text });
      return undefined;
    }
    this.#lastUtterance = text;
    onEvent({ type: 'user', text });
    onEvent({ type: 'busy', on: true });
    const emit = (event: AgentEvent) => onEvent({ type: 'agent', event });
    try {
      let result: TurnResult;
      if (this.llm) {
        try {
          result = await this.llm.turn(text, emit);
        } catch (err) {
          onEvent({ type: 'system', text: `Model unavailable (${(err as Error).message.slice(0, 80)}); using the offline planner.` });
          result = await this.offline.turn(text, emit);
        }
      } else result = await this.offline.turn(text, emit);
      // The speaker changed mid-turn (this host was closed): nothing was done, and there is nothing to say.
      if (this.#closed) {
        onEvent({ type: 'system', text: 'Speaker changed, so nothing was done.' });
        return undefined;
      }
      onEvent({ type: 'alexa', text: result.speech, planner: result.planner, latencyMs: result.latencyMs });
      return result;
    } finally {
      onEvent({ type: 'busy', on: false });
    }
  }

  /** Tool calls from an MCP Apps view (the plan card refreshing itself) go through the same client, quietly. */
  async viewCallTool(name: string, args: Record<string, unknown>) {
    return this.client.callTool(name, args, { quiet: name === 'plan_status' });
  }

  readResource(uri: string) {
    return this.client.readResource(uri);
  }

  close() {
    this.#closed = true;
    this.#pendingAnswer?.({ action: 'cancel' });
    return this.client.close();
  }
}
